import { Button, Spinner } from '@crm/ui';
import { Megaphone } from 'lucide-react';
import { Link } from 'react-router-dom';
import { CAMPAIGNS_ROUTES, campaignPath, useCampaigns } from '../api';

/** Os degraus da campanha, do contato ate o dinheiro na mesa. */
const COLUNAS = [
  { key: 'leadsTotal', label: 'Leads' },
  { key: 'contactedTotal', label: 'Contatos' },
  { key: 'scheduledTotal', label: 'Agendamentos' },
  { key: 'attendedTotal', label: 'Comparecimentos' },
] as const;

/**
 * As campanhas que estao no ar e o que cada uma trouxe. Tres bastam: o painel
 * responde "como estamos indo", nao substitui a tela de campanhas.
 *
 * Mora no rodape do box de conversao, sob um risco: campanha e a origem do
 * funil desenhado logo acima, e uma moldura propria fazia parecer outro
 * assunto. O risco sai daqui porque o bloco inteiro some quando nada esta no
 * ar - vindo do box, sobraria uma linha solta.
 *
 * As colunas sao acumuladas, na ordem do funil - quem agendou tambem foi
 * contatado. Lidas da esquerda para a direita, mostram onde a campanha perde
 * gente.
 */
export function CampaignsWidget() {
  const campaigns = useCampaigns();
  const ativas = (campaigns.data?.data ?? [])
    .filter((item) => item.status === 'ACTIVE')
    .slice(0, 3);

  if (!campaigns.isPending && ativas.length === 0) return null;

  return (
    <section className="space-y-4 border-t border-border pt-6">
      <div className="flex items-center justify-between gap-2">
        <h2 className="font-medium">Campanhas ativas</h2>
        <Button size="sm" variant="ghost" asChild>
          <Link to={CAMPAIGNS_ROUTES.LIST}>
            <Megaphone className="h-4 w-4" />
            Ver mais
          </Link>
        </Button>
      </div>

      {campaigns.isPending ? (
        <div className="flex justify-center py-6">
          <Spinner />
        </div>
      ) : (
        /* A tabela rola sozinha no celular: quatro colunas de numero nao
           cabem em tela estreita, e encolher a fonte so piora. */
        <div className="-mx-2 overflow-x-auto px-2">
          <table className="w-full min-w-[34rem] border-collapse text-sm">
            <thead>
              <tr className="border-b border-border text-xs uppercase tracking-wide text-muted-foreground">
                <th className="py-2 pr-3 text-left font-medium">Campanha</th>
                {COLUNAS.map((coluna) => (
                  <th key={coluna.key} className="px-3 py-2 text-right font-medium">
                    {coluna.label}
                  </th>
                ))}
              </tr>
            </thead>
            <tbody>
              {ativas.map((campaign) => (
                <tr
                  key={campaign.id}
                  className="border-b border-border last:border-0 transition-colors hover:bg-muted/50"
                >
                  <td className="max-w-0 py-2.5 pr-3">
                    <Link to={campaignPath(campaign.id)} className="block truncate hover:underline">
                      {campaign.name}
                    </Link>
                  </td>
                  {COLUNAS.map((coluna) => (
                    <td
                      key={coluna.key}
                      className="px-3 py-2.5 text-right tabular-nums text-muted-foreground"
                    >
                      {campaign[coluna.key]}
                    </td>
                  ))}
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}
